import { EventSeverity, NotificationRule } from '@eppendorf/vnls-notifications-utils';
import {
  Badge,
  Button,
  Icon,
  IconSizeClasses,
  SortableTable,
  useDidMountEffect,
} from '@eppendorf/vnls-react-components';
import { createColumnHelper } from '@tanstack/react-table';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError, useSearchParams } from 'react-router-dom';

import type { Pagination as PaginationType } from '$shared/types';

import { FeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { Pagination } from '$components/table/pagination/pagination';
import { searchDeviceIcon } from '$features/devices/devices.utils';
import { getEventIconName } from '$features/monitoring/events/events.utils';
import { isValueWithKeyInSearchParams } from '$features/navigation/isValueWithKeyInSearchParams';
import { AddNotificationRuleDialog } from '$features/notifications/add-or-edit-notification-rule-dialog';
import {
  useAddedNotificationRule,
  useNotificationRulesWithQueryParams,
} from '$features/notifications/notification-rules.api';
import { PauseNotificationRuleToggle } from '$features/notifications/pause-notification-rule-toggle';

import './notification-rule-list.scss';

export function NotificationRuleList(): ReactElement {
  const navigate = useNavigate();
  const routeError = useRouteError();
  const [{ page, pageSize }, setPagination] = useState<PaginationType>({
    page: 1,
    pageSize: 10,
  });

  const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState(false);

  const { t } = useTranslation();

  const {
    data: paginatedData,
    isError,
    isLoading,
    refetch,
  } = useNotificationRulesWithQueryParams({ page, pageSize });

  const { data: addQueryState } = useAddedNotificationRule();

  useDidMountEffect(() => {
    refetch();
  }, [page, pageSize]);

  const notifications = paginatedData ? paginatedData.data : [];

  useEffect(() => {
    if (addQueryState) {
      setPagination({ pageSize, page: 1 });
    }
  }, [addQueryState]);

  function handleRowClick(notification: NotificationRule) {
    navigate({
      search: `?sidecarRoute=notifications&id=${notification.id}`,
    });
  }

  const handlePageSizeChange = (newPageSize: string) => {
    setPagination({ page: 1, pageSize: Number(newPageSize) });
  };

  const handlePageChange = (newPage: number) => {
    setPagination({ pageSize, page: newPage });
  };

  const columnHelper = createColumnHelper<NotificationRule>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('paused', {
        header: () => t('shared.status'),
        meta: { dataLabel: t('shared.status') },
        // eslint-disable-next-line react/no-unstable-nested-components -- its ok
        cell: (info) => {
          const isPaused = info.getValue() ?? false;
          const statusBadge = (
            <Badge color={isPaused ? 'orange' : 'green'}>
              {t(isPaused ? 'notificationRules.paused' : 'notificationRules.active')}
            </Badge>
          );
          return (
            <FeatureIsEnabled
              feature="ESEVNLS_4578_PASUSE_NOTI_RULES"
              fallback={statusBadge}
            >
              <PauseNotificationRuleToggle notificationRule={info.row.original} />
            </FeatureIsEnabled>
          );
        },
      }),
      columnHelper.accessor('name', {
        header: () => t('notificationRulesTable.name'),
        meta: { dataLabel: t('notificationRulesTable.name') },
        cell: (info) => info.renderValue(),
      }),
      columnHelper.accessor('selectedDeviceType', {
        header: () => t('deviceTable.type'),
        meta: { dataLabel: t('deviceTable.type') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const deviceType = info.getValue() ?? 'freezer';
          const { subjects } = info.row.original;
          const filteredSubjects = subjects.filter(
            (subject) => subject.type === 'device',
          );
          return (
            <div className="flex flex__ai--center">
              <div className="flex flex__ai--center gap-s">
                <div className="w-3xs h-3xs">
                  <Icon
                    name={searchDeviceIcon(deviceType)}
                    size={IconSizeClasses.Small}
                  />
                </div>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- we know it exists */}
                {t(`deviceType.${deviceType}` as any)}
              </div>
              <Badge className="m-left-s" color="blue" size="small">
                {filteredSubjects.length.toString()}
              </Badge>
            </div>
          );
        },
      }),
      columnHelper.accessor('tags', {
        header: () => t('notificationRulesTable.condition'),
        meta: { dataLabel: t('notificationRulesTable.condition') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const tags = info.getValue();
          const hasAdvancedConditions = info.row.original.categories?.some(
            (category) => category.subCategories.length > 0,
          );

          if (hasAdvancedConditions) {
            return (
              <span className="font-weight-semibold">
                {t('tagsCondition.modes.advanced')}
              </span>
            );
          }

          return (
            <div className="flex flex-wrap flex__jc--flex-start gap-m">
              {tags &&
                Object.values(EventSeverity)
                  .filter((tag) => tags.includes(tag))
                  .map((tag) => (
                    <span key={`tag-${info.getValue()}-${info.row.id}-${tag}`}>
                      <Icon
                        name={`event-${getEventIconName(tag)}`}
                        size={IconSizeClasses.XSmall}
                      />
                    </span>
                  ))}
            </div>
          );
        },
      }),
      columnHelper.accessor('escalationLayers', {
        header: () => t('notificationRulesTable.recipients'),
        meta: { dataLabel: t('notificationRulesTable.recipients') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => {
          const escalationLayers = info.getValue() || [];

          return (
            <div className="flex flex__ai--center">
              <span
                className="ellipsize ellipsize--wrap-on-mobile"
                title={escalationLayers[0].recipients[0].name}
              >
                {escalationLayers[0].recipients[0].name}
              </span>
              {escalationLayers[0].recipients.length > 1 && (
                <Badge className="m-left-s whitespace-nowrap" color="blue" size="small">
                  {`+ ${(escalationLayers[0].recipients.length - 1).toString()}`}
                </Badge>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('createdBy', {
        header: () => t('notificationRulesTable.createdBy'),
        meta: { dataLabel: t('notificationRulesTable.createdBy') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => (
          <span
            className="ellipsize ellipsize--wrap-on-mobile"
            title={info.getValue() ?? ''}
          >
            {info.renderValue()}
          </span>
        ),
      }),
    ],
    [],
  );

  const [searchParams] = useSearchParams();

  return (
    <>
      {isNotificationDialogOpen && (
        <AddNotificationRuleDialog
          isOpen={isNotificationDialogOpen}
          onOpenChange={(open) => setIsNotificationDialogOpen(open)}
        />
      )}

      {(routeError || isError) && <LoadingErrorHint />}

      <div className="p-bottom-xxl p-top-m">{t('notificationRules.hint')}</div>

      {!isLoading && !isError && (
        <>
          {notifications && !isLoading && paginatedData?.pagination && (
            <>
              <SortableTable<NotificationRule[], NotificationRule>
                data={notifications}
                columns={columns}
                onRowClick={(notification) => handleRowClick(notification)}
                isSelectedRow={(notification) =>
                  isValueWithKeyInSearchParams('id', notification.id, searchParams)
                }
                isTableToCardDesign
                className="table--notification-rule-list"
              />
              {notifications.length > 0 && (
                <Pagination
                  paginationData={paginatedData.pagination}
                  onPageSizeChange={(size) => handlePageSizeChange(size)}
                  onPageChange={(newPage) => handlePageChange(newPage)}
                />
              )}
            </>
          )}

          {!isLoading && !isError && (!notifications || notifications.length === 0) && (
            <div className="box flex flex__dir--column flex__ai--center p-xxxl">
              <p className="m-bottom-xxl">{t('notificationRulesTable.emptyState')}</p>

              <Button size="small" onClick={() => setIsNotificationDialogOpen(true)}>
                <Icon
                  name="plus"
                  className="bg-white m-right-xs"
                  size={IconSizeClasses.Small}
                />
                {t('notificationRules.add')}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
